const ResizePlugin = (slider) => {
  const observer = new ResizeObserver(function () {
    slider.update();
  });

  slider.on('created', () => {
    observer.observe(slider.container);
  });

  slider.on('destroyed', () => {
    observer.unobserve(slider.container);
  });
};

class KeenSliderComponent extends HTMLElement {
  constructor() {
    super();
    this.sliderId = this.querySelector('[id^="Slider-"]')?.id;
    this.sliderThumbnailsId = this.querySelector('[id^="Slider-Thumbnails-"]')?.id;
    this.currentCounter = this.querySelector('.slider-counter--current');
    this.counterDots = this.querySelectorAll('.slider-counter__link');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.autoplayButtonIsSetToPlay = false;
    const plugins = this.dataset.responsive === 'true' ? [ResizePlugin] : [];
    this.adaptiveHeight = this.dataset.adaptiveHeight || false;
    const isMobileDisabled = this.dataset.sliderMobileDisabled === 'true';
    const isTabletDisabled = this.dataset.sliderTabletDisabled === 'true';
    const isDesktopDisabled = this.dataset.sliderDesktopDisabled === 'true';
    const isDesktopLargeDisabled = this.dataset.sliderDesktopLargeDisabled === 'true';

    this.slider = new KeenSlider(
      `#${this.sliderId}`,
      {
        loop: this.dataset.sliderLoop == 'true' ? true : false,
        slides: {
          origin: this.dataset.slidesOriginMobile ? this.dataset.slidesOriginMobile : this.dataset.slidesOrigin,
          perView: this.dataset.slidesPerViewMobile,
          spacing: this.dataset.sliderGapMobile,
        },
        disabled: isMobileDisabled || false,
        breakpoints: {
          '(min-width: 750px)': {
            slides: {
              origin: this.dataset.slidesOriginTablet ? this.dataset.slidesOriginTablet : this.dataset.slidesOrigin,
              perView: this.dataset.slidesPerViewTablet || this.dataset.slidesPerViewDesktop,
              spacing: this.dataset.sliderGapMobile || this.dataset.sliderGapDesktop,
            },
            disabled: isTabletDisabled || false,
          },
          '(min-width: 990px)': {
            slides: {
              origin: this.dataset.slidesOriginDesktop ? this.dataset.slidesOriginDesktop : this.dataset.slidesOrigin,
              perView: this.dataset.slidesPerViewDesktop,
              spacing: this.dataset.sliderGapDesktop,
            },
            disabled: isDesktopDisabled || false,
          },
          '(min-width: 1800px)': {
            slides: {
              origin: this.dataset.slidesOriginDesktopLarge
                ? this.dataset.slidesOriginDesktopLarge
                : this.dataset.slidesOrigin,
              perView: this.dataset.slidesPerViewDesktopLarge
                ? this.dataset.slidesPerViewDesktopLarge
                : this.dataset.slidesPerViewDesktop,
              spacing: this.dataset.sliderGapDesktopLarge
                ? this.dataset.sliderGapDesktopLarge
                : this.dataset.sliderGapDesktop,
            },
            disabled: isDesktopLargeDisabled || false,
          },
        },
        created: (sliderInstance) => {
          sliderInstance.container.classList.add('slider-created');
          this.resetButtonsVisibility();
        },
        updated: (sliderInstance) => {
          this.resetButtonsVisibility();
        },
      },
      plugins
    );

    // MutationObserver to update slider on DOM changes (slides added/removed)
    const slideList = this.querySelector(`#${this.sliderId}`);
    if (slideList) {
      this._mutationObserver = new MutationObserver(() => {
        this.slider.update();
      });
      this._mutationObserver.observe(slideList, {
        childList: true, // Watch for added/removed slides
        subtree: false, // Only direct children (the slides)
      });
    }

    if (this.dataset.hasThumbnailSlider) {
      this.thumbnails = new KeenSlider(
        `#${this.sliderThumbnailsId}`,
        {
          initial: 0,
          slides: {
            perView: this.dataset.thumbnailSlidesPerViewDesktop,
            spacing: this.dataset.thumbnailSliderGapMobile,
          },
          breakpoints: {
            '(min-width: 750px)': {
              slides: {
                perView: this.dataset.thumbnailSlidesPerViewTablet,
                spacing: this.dataset.thumbnailSliderGapTablet,
              },
            },
          },
        },
        [this.createThumbnailPlugin(this.slider)]
      );
    }

    this.prevButton?.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton?.addEventListener('click', this.onButtonClick.bind(this));
    this.counterDots?.forEach((counter, index) => {
      counter.addEventListener('click', () => {
        this.onCounterClick(index);
      });
    });
    this.slider.on('slideChanged', this.onSlideChanged.bind(this));
    this.init();
  }

  init() {
    this.counterDotsUpdateProgress();
    if (this.adaptiveHeight && window.matchMedia('(max-width: 749px)').matches) this.updateHeight();

    this.handleVideoPlayback();

    // defer autoplay until full page is loaded
    if (this.dataset.sliderAutoplay == 'true') {
      window.addEventListener('load', () => {
        this.setAutoPlay();
      });
    }
  }

  updateHeight = () => {
    this.slider.container.style.height =
      this.slider.slides[this.slider.track.details?.rel]?.querySelector('.product-media-container').offsetHeight + 'px';
  };

  createThumbnailPlugin(main) {
    return (slider) => {
      function removeActive() {
        slider.slides.forEach((slide) => {
          slide.classList.remove('active');
        });
      }
      function addActive(idx) {
        slider.slides[idx].classList.add('active');
      }

      function addClickEvents() {
        slider.slides.forEach((slide, idx) => {
          slide.addEventListener('click', () => {
            main.moveToIdx(idx);
          });
        });
      }

      slider.on('created', () => {
        addActive(slider.track.details.rel);
        addClickEvents();
        main.on('animationStarted', (main) => {
          removeActive();
          const next = main.animator.targetIdx || 0;
          addActive(main.track.absToRel(next));
          slider.moveToIdx(Math.min(slider.track.details.maxIdx, next));
        });
      });
    };
  }

  onButtonClick(event) {
    if (this.dataset.sliderMoveEntireRow == 'true') {
      this.moveEntireRow(event.currentTarget.name);
      return;
    }

    this.moveOneSlide(event.currentTarget.name);
  }

  moveOneSlide(target) {
    const { rel, minIdx, maxIdx } = this.slider.track.details;
    if (target === 'next') {
      if (maxIdx === rel) return;
      if (maxIdx !== Infinity) this.updateButtonsVisibility(rel + 1);
      this.slider.moveToIdx(rel + 1);
    }
    if (target === 'previous') {
      if (minIdx === rel) return;
      if (minIdx !== -Infinity) this.updateButtonsVisibility(rel - 1);
      this.slider.moveToIdx(rel - 1);
    }
  }

  moveEntireRow(target) {
    let nextSlideIdx;
    let sliderLoop = this.dataset.sliderLoop;
    if (target === 'next') {
      if (sliderLoop == 'true') {
        nextSlideIdx = this.slider.track.details.rel + Math.floor(this.slider.options.slides.perView);
      } else {
        nextSlideIdx =
          this.slider.track.details.maxIdx <
          this.slider.track.details.rel + Math.floor(this.slider.options.slides.perView)
            ? this.slider.track.details.maxIdx
            : this.slider.track.details.rel + Math.floor(this.slider.options.slides.perView);
      }
      this.slider.moveToIdx(nextSlideIdx);
    }

    if (target === 'previous') {
      if (sliderLoop == 'true') {
        nextSlideIdx = this.slider.track.details.rel - Math.floor(this.slider.options.slides.perView);
      } else {
        nextSlideIdx =
          this.slider.track.details.rel - Math.floor(this.slider.options.slides.perView) < 0
            ? 0
            : this.slider.track.details.rel - Math.floor(this.slider.options.slides.perView);
      }
      this.slider.moveToIdx(nextSlideIdx);
    }
  }

  onCounterClick(index) {
    this.slider.moveToIdx(index);
  }

  onSlideChanged(event) {
    // Only reset the autoplay if autoplay is enabled and the user manually changed slides
    if (this.dataset.sliderAutoplay == 'true' && this.autoplayButtonIsSetToPlay) {
      this.resetAutoplay();
    }

    if (this.currentCounter) {
      this.currentCounter.textContent = this.slider.track.details.rel + 1;
    }

    if (this.slider.track.details.abs > 0) {
      this.classList.add('fade_left');
    } else {
      this.classList.remove('fade_left');
    }

    if (this.slider.track.details.abs == this.slider.track.details.maxIdx) {
      this.classList.remove('fade_right');
    } else {
      this.classList.add('fade_right');
    }

    if (this.counterDots.length > 0) {
      this.counterDots.forEach((dot) => {
        dot.classList.remove('slider-counter__link--active');
        dot.removeAttribute('aria-current');
      });
      this.counterDots[this.slider.track.details.rel].classList.add('slider-counter__link--active');
      this.counterDots[this.slider.track.details.rel].setAttribute('aria-current', 'true');
    }

    if (this.prevButton?.classList.contains('slider-button--visually-hidden') && this.slider.track.details.rel != 0) {
      this.prevButton?.classList.remove('slider-button--visually-hidden');
    }

    this.counterDotsUpdateProgress();
    if (this.adaptiveHeight && window.matchMedia('(max-width: 749px)').matches) this.updateHeight();

    this.handleVideoPlayback();
  }

  handleVideoPlayback() {
    if (!this.slider?.slides || !this.slider.track?.details) return;

    const currentSlide = this.slider.slides[this.slider.track.details.rel];
    if (!currentSlide) return;

    this.pauseAllVideos();

    const video = currentSlide.querySelector('video');
    if (video && video.hasAttribute('autoplay')) {
      setTimeout(() => {
        if (this.slider.track.details.rel === this.slider.track.details.abs) {
          video.play().catch((error) => {
            console.warn('Video autoplay failed:', error);
          });
        }
      }, 100);
    }
  }

  pauseAllVideos() {
    const allVideos = this.querySelectorAll('video');
    allVideos.forEach((video) => {
      if (!video.paused) {
        video.pause();
      }
    });
  }

  // Handle resetting the autoplay timer
  resetAutoplay() {
    clearInterval(this.autoplay);
    if (this.autoplayButtonIsSetToPlay) {
      this.play();
    }
  }

  setAutoPlay() {
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton?.addEventListener('click', this.autoPlayToggle.bind(this));
    }
    this.autoplayButtonIsSetToPlay = true;
    this.play();
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton && document.activeElement !== this.sliderAutoplayButton) {
      if (!this.autoplayButtonIsSetToPlay) return;
      this.play();
    }
  }

  focusInHandling(event) {
    if (event.target.classList.contains('slider-button') || event.target.closest('.slider-button')) {
      return;
    }

    if (this.sliderAutoplayButton && event.target === this.sliderAutoplayButton) {
      if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    }
  }

  resetButtonsVisibility() {
    if (!this.slider || !this.prevButton || !this.nextButton) return;

    const allSlidesFit = this.slider.slides.length <= this.slider.options.slides.perView;
    this.slider.container.classList.toggle('slider--all-slides-fit', allSlidesFit);
    if (allSlidesFit) {
      this.prevButton.classList.add('slider-button--visually-hidden');
      this.nextButton.classList.add('slider-button--visually-hidden');
      return;
    }

    if (this.slider.options.loop === false) {
      const { rel, minIdx, maxIdx } = this.slider.track.details;
      this.prevButton.classList.toggle('slider-button--visually-hidden', rel === minIdx);
      this.nextButton.classList.toggle('slider-button--visually-hidden', rel === maxIdx);
    } else {
      // buttons visible for looped (infinite) slider
      this.prevButton.classList.remove('slider-button--visually-hidden');
      this.nextButton.classList.remove('slider-button--visually-hidden');
    }
  }
  updateButtonsVisibility(relativeIndex) {
    const { minIdx, maxIdx } = this.slider.track.details;
    this.prevButton.classList.toggle('slider-button--visually-hidden', relativeIndex === minIdx);
    this.nextButton.classList.toggle('slider-button--visually-hidden', relativeIndex === maxIdx);
  }
  play() {
    const allSlidesFit = this.slider.slides.length <= this.slider.options.slides.perView;
    if (allSlidesFit) {
      this.pause();
      return;
    }
    this.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(() => {
      if (this.dataset.sliderMoveEntireRow == 'true') {
        this.moveEntireRow('next');
        return;
      }
      this.moveOneSlide('next');
    }, this.dataset.sliderAutoplaySpeed);
  }

  pause() {
    this.setAttribute('aria-live', 'polite');
    this.autoplay = clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  counterDotsUpdateProgress() {
    if (this.counterDots.length < 1) return;
    if (this.dataset.sliderAutoplay === 'false' || !this.autoplayButtonIsSetToPlay) return;

    clearInterval(this.countdown);
    this.sliderDotProgress = this.querySelector('.slider-counter__link--active .dot-progress');
    this.sliderDotProgress.style.width = 0;

    let max = Number(this.dataset.sliderAutoplaySpeed);
    let width = 0;

    this.countdown = setInterval(() => {
      if (this.autoplay === undefined) return;
      if (width < max && parseInt(this.sliderDotProgress.style.width) < 100) {
        width = width + 50;
        let progress = (100 / max) * width;
        this.sliderDotProgress.style.width = `${progress}%`;
      }
    }, 50);
  }

  disconnectedCallback() {
    this.pauseAllVideos();
    if (this._mutationObserver) {
      this._mutationObserver.disconnect();
    }
  }
}

customElements.define('keen-slider-component', KeenSliderComponent);

/*
  Simple and lightweight auto-scrolling vertical slider,
  leveraging the native CSS scroll-snap feature for smooth transitions.

  ## Basic HTML Structure:
  <div class="slider-simple__slides slider-simple--vertical js-slider-simple--vertical">
    <div class="slider-simple__slide">Slide 1</div>
    <div class="slider-simple__slide">Slide 2</div>
    <div class="slider-simple__slide">Slide 3</div>
  </div>
*/
// don’t start until page is interactive
document.addEventListener('DOMContentLoaded', function () {
  const sliders = document.querySelectorAll('.js-slider-simple--vertical');
  sliders.forEach((slider) => {
    if (!slider || slider.children.length < 2) return;
    const slideHeight = slider.offsetHeight;
    const slideCount = slider.children.length;
    let currentIndex = 0;

    function autoScroll() {
      currentIndex = (currentIndex + 1) % slideCount;
      slider.scrollTo({ top: currentIndex * slideHeight, behavior: 'smooth' });
    }

    setInterval(autoScroll, 5000);
  });
});
